<template>
    <div
        class="
            absolute
            top-[70px]
            right-[10px]
            w-[380px]
            h-[calc(100vh-160px)]
            min-h-[400px]
            bg-white
            border-2 border-solid border-[#e8e8e8]
            rounded-xl
            shadow-2xl
            z-[105]
            flex flex-col
            overflow-hidden
        "
    >
        <!-- Header -->
        <div
            class="
                p-4
                bg-gray-50
                border-b border-gray-100
                flex
                justify-between
                items-center
            "
        >
            <div class="flex items-center gap-2 flex-wrap">
                <img
                    src="/default-images/whiteboard/tool-ai.svg"
                    width="24"
                    height="24"
                    alt="AI Icon"
                />
                <span class="font-bold text-gray-800 text-base"
                    >AI Play Assistant</span
                >
                <span
                    v-if="totalTokensUsed > 0"
                    class="
                        text-[10px]
                        font-bold
                        text-purple-600
                        bg-purple-50
                        px-1.5
                        py-0.5
                        rounded
                    "
                    title="Total tokens consumed in this session"
                >
                    {{ totalTokensUsed.toLocaleString() }} tokens
                </span>
            </div>
            <button
                type="button"
                @click="$emit('close')"
                class="text-gray-400 hover:text-gray-600 font-bold text-lg p-1"
            >
                &times;
            </button>
        </div>

        <!-- Chat History -->
        <div
            ref="aiChatHistoryRef"
            class="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50"
        >
            <div
                v-for="(msg, index) in aiMessages"
                :key="index"
                :class="[
                    'flex flex-col max-w-[85%] rounded-lg p-3 text-sm shadow-sm relative group',
                    msg.role === 'user'
                        ? 'bg-[#1e3a8a] text-white ml-auto rounded-tr-none'
                        : 'bg-white text-gray-800 border border-gray-100 mr-auto rounded-tl-none',
                ]"
            >
                <div
                    class="whitespace-pre-line leading-relaxed"
                    v-html="msg.text"
                ></div>
                <!-- Individual Message Token Usage -->
                <span
                    v-if="msg.total_tokens"
                    class="
                        text-[8px]
                        opacity-40
                        mt-1
                        block
                        text-right
                        font-sans
                    "
                >
                    {{ msg.total_tokens.toLocaleString() }} tokens
                </span>
            </div>
            <div
                v-if="isAiThinking"
                class="
                    bg-white
                    text-gray-500
                    border border-gray-100
                    mr-auto
                    rounded-lg rounded-tl-none
                    p-3
                    text-sm
                    shadow-sm
                    flex
                    items-center
                    gap-2
                    max-w-[85%]
                "
            >
                <span class="animate-pulse">Assistant is thinking...</span>
            </div>
        </div>

        <!-- Input Box -->
        <form
            @submit.prevent="sendAiMessage"
            class="
                p-3
                border-t border-gray-100
                bg-white
                flex
                gap-2
                items-center
            "
        >
            <input
                v-model="aiInput"
                type="text"
                placeholder="Ask AI to modify elements..."
                class="
                    flex-1
                    border border-gray-200
                    rounded-lg
                    px-3
                    py-2
                    text-sm
                    focus:outline-none focus:border-[#1e3a8a]
                    bg-gray-50
                    focus:bg-white
                    transition-colors
                "
                :disabled="isAiThinking"
            />
            <markdown-upload-button
                size="sm"
                :disabled="isAiThinking"
                @upload="
                    (t) =>
                        (aiInput = (aiInput ? aiInput.trim() + '\n\n' : '') + t)
                "
            ></markdown-upload-button>
            <mic-button
                size="sm"
                :disabled="isAiThinking"
                @listen-start="
                    voiceBaseAi = aiInput ? aiInput.trim() + ' ' : ''
                "
                @transcript="(t) => (aiInput = voiceBaseAi + t)"
                @listen-end="onVoiceEnd($event)"
            ></mic-button>
            <button
                v-if="isAiThinking"
                type="button"
                @click="killAiAction"
                class="
                    bg-red-500
                    hover:bg-red-600
                    text-white
                    rounded-lg
                    px-4
                    py-2
                    text-sm
                    font-semibold
                    transition-colors
                    shadow-md
                    flex
                    items-center
                    justify-center
                "
                title="Kill AI Process"
            >
                <ion-icon name="stop-outline"></ion-icon>
            </button>
            <button
                v-else
                type="submit"
                class="
                    bg-[#1e3a8a]
                    hover:bg-blue-800
                    text-white
                    rounded-lg
                    px-4
                    py-2
                    text-sm
                    font-semibold
                    transition-colors
                    shadow-md
                    disabled:opacity-50
                    flex
                    items-center
                    justify-center
                "
                :disabled="isAiThinking || !aiInput.trim()"
            >
                <ion-icon name="paper-plane-outline"></ion-icon>
            </button>
        </form>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "WhiteboardAiDrawer",
    props: {
        play: {
            type: Object,
            default: null,
        },
        roomId: {
            type: String,
            default: null,
        },
        team: {
            type: Object,
            default: null,
        },
        drawBuffers: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            aiMessages: [],
            aiInput: "",
            isAiThinking: false,
            voiceBaseAi: "",
            aiCancelTokenSource: null,
            totalTokensUsed: 0,
        };
    },
    mounted() {
        this.fetchChatHistory();
    },
    watch: {
        play: {
            handler() {
                this.fetchChatHistory();
            },
            deep: true,
        },
    },
    methods: {
        fetchChatHistory() {
            const playId = this.play?.play_id || null;
            if (!playId) {
                this.aiMessages = [];
                this.totalTokensUsed = 0;
                return;
            }

            axios
                .get("/plays/ai-history", {
                    params: { play_id: playId },
                })
                .then((response) => {
                    this.aiMessages = response.data.messages || [];
                    this.totalTokensUsed = response.data.total_tokens_used || 0;
                    this.scrollToBottom();
                })
                .catch((err) => {
                    console.error(
                        "Failed to fetch whiteboard AI history:",
                        err
                    );
                });
        },
        scrollToBottom() {
            this.$nextTick(() => {
                if (this.$refs.aiChatHistoryRef) {
                    this.$refs.aiChatHistoryRef.scrollTop =
                        this.$refs.aiChatHistoryRef.scrollHeight;
                }
            });
        },
        killAiAction() {
            axios.post("/plays/kill-ai").catch((err) => {
                console.error("Failed to send server-side kill signal:", err);
            });

            if (this.aiCancelTokenSource) {
                this.aiCancelTokenSource.cancel(
                    "Operation canceled by the user."
                );
                this.aiCancelTokenSource = null;
            }
            this.isAiThinking = false;
        },
        onVoiceEnd(reason) {
            if (reason === "error") return;
            setTimeout(() => {
                if (this.isAiThinking) return;
                if (this.aiInput.value?.trim() || this.aiInput.trim()) {
                    this.sendAiMessage();
                }
            }, 50);
        },
        async sendAiMessage() {
            const text = this.aiInput.trim();
            if (!text) return;

            this.aiMessages.push({ role: "user", text });
            this.aiInput = "";
            this.isAiThinking = true;
            this.scrollToBottom();

            this.aiCancelTokenSource = axios.CancelToken.source();

            try {
                const payload = {
                    play_id: this.play?.play_id || null,
                    meeting_id: this.roomId || null,
                    message: text,
                    play_json: JSON.stringify(this.drawBuffers),
                    chat_history: this.aiMessages.slice(-15).map((m) => ({
                        role: m.role,
                        text: m.text,
                    })),
                };

                const response = await axios.post("/plays/ai-action", payload, {
                    cancelToken: this.aiCancelTokenSource.token,
                });

                if (response.data && response.data.success) {
                    // Refresh history from db to get exact bot messages & token usage stats
                    this.fetchChatHistory();

                    if (
                        response.data.actions &&
                        response.data.actions.length > 0
                    ) {
                        this.$emit("actions", response.data.actions);
                    }
                } else {
                    this.aiMessages.push({
                        role: "assistant",
                        text: "Sorry, I couldn't process that request properly.",
                    });
                }
            } catch (err) {
                if (axios.isCancel(err)) {
                    console.log("AI Action request was cancelled by user.");
                    this.aiMessages.push({
                        role: "assistant",
                        text: "AI process cancelled by user.",
                    });
                    return;
                }
                console.error("AI Action Error:", err);
                const errMsg =
                    err.response?.data?.error ||
                    "An error occurred while communicating with the AI service.";
                this.aiMessages.push({
                    role: "assistant",
                    text: `Error: ${errMsg}`,
                });
            } finally {
                this.isAiThinking = false;
                this.aiCancelTokenSource = null;
                this.scrollToBottom();
            }
        },
    },
};
</script>
